import type {
  ParticipantBehaviour,
  ParticipantBehaviourRecording,
} from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  createColumnHelper,
  DataTable,
  Empty,
  FancyDate,
  Pagination,
  type PaginationOptions,
  ScrollArea,
} from '@piccolohealth/ui';
import { DateTime, P } from '@piccolohealth/util';
import React from 'react';
import { UserDescription } from '../../../components/user/UserDescription';
import { FLOOR_PERIOD } from '../../participantData/utils';
import { ParticipantBehaviourRecordingDeleteButton } from './ParticipantBehaviourRecordingDeleteButton';
import { ParticipantBehaviourRecordingEditButton } from './ParticipantBehaviourRecordingEditButton';
import { ParticipantBehaviourRecordingTypeTag } from './ParticipantBehaviourRecordingTypeTag';
import { ParticipantBehaviourRecordingValue } from './ParticipantBehaviourRecordingValue';

type ParticipantBehaviourRecordingWithBehaviour = ParticipantBehaviourRecording & {
  participantBehaviour: ParticipantBehaviour;
};

interface Props {
  participantId: string;
  participantBehaviours: ParticipantBehaviour[];
}

export const ParticipantBehaviourRecordingsTable = (props: Props) => {
  const { participantId, participantBehaviours } = props;

  const [paginationState, setPaginationState] = React.useState({
    currentPage: 0,
    pageSize: 50,
  });

  const recordings = React.useMemo(() => {
    const recordings: ParticipantBehaviourRecordingWithBehaviour[] = participantBehaviours.flatMap(
      (b) => b.recordings.map((r) => ({ ...r, participantBehaviour: b })),
    );

    return P.orderBy(recordings, (recording) => recording.timestamp, 'desc');
  }, [participantBehaviours]);

  const orderedGroupedRecordings = React.useMemo(() => {
    const pageRecordings = recordings.slice(
      paginationState.currentPage * paginationState.pageSize,
      paginationState.currentPage * paginationState.pageSize + paginationState.pageSize,
    );

    const groupedByDay = P.groupBy(pageRecordings, (r) =>
      DateTime.fromISO(r.timestamp.toString()).startOf(FLOOR_PERIOD).toISO(),
    );

    const outerOrdered = P.orderBy(
      Object.entries(groupedByDay),
      ([timestamp]) => DateTime.fromISO(timestamp),
      'desc',
    );

    return outerOrdered.map(([timestamp, recordings]) => [timestamp, recordings ?? []] as const);
  }, [paginationState, recordings]);

  const nextPage = React.useCallback(() => {
    setPaginationState((oldPaginationState) => ({
      ...oldPaginationState,
      currentPage: oldPaginationState.currentPage + 1,
    }));
  }, []);

  const previousPage = React.useCallback(() => {
    setPaginationState((oldPaginationState) => ({
      ...oldPaginationState,
      currentPage: oldPaginationState.currentPage - 1,
    }));
  }, []);

  const onPageSizeChange = React.useCallback((pageSize: number) => {
    setPaginationState({ currentPage: 0, pageSize });
  }, []);

  const total = recordings.length;
  const hasNextPage = paginationState.currentPage < Math.ceil(total / paginationState.pageSize) - 1;
  const hasPreviousPage = paginationState.currentPage > 0;

  const pagination: PaginationOptions = {
    currentPage: paginationState.currentPage,
    pageSize: paginationState.pageSize,
    total,
    hasNextPage,
    hasPreviousPage,
    pageSizeOptions: [25, 50, 100],
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} recordings`,
    nextPage,
    previousPage,
    onPageSizeChange,
  };

  const columns = React.useMemo(() => {
    const columnHelper = createColumnHelper<ParticipantBehaviourRecordingWithBehaviour>();

    return [
      columnHelper.display({
        header: 'Behaviour',
        minSize: 200,
        maxSize: 400,
        cell: (ps) => {
          return (
            <ChakraV3.Text lineClamp={2}>{ps.row.original.participantBehaviour.name}</ChakraV3.Text>
          );
        },
      }),
      columnHelper.display({
        header: 'Type',
        minSize: 150,
        maxSize: 200,
        cell: (ps) => {
          return <ParticipantBehaviourRecordingTypeTag type={ps.row.original.__typename} />;
        },
      }),
      columnHelper.display({
        header: 'User',
        minSize: 240,
        maxSize: 300,
        cell: (ps) => {
          return (
            <UserDescription
              name={ps.row.original.user.name}
              secondary={ps.row.original.user.email}
              picture={
                ps.row.original.user.__typename === 'User'
                  ? ps.row.original.user.picture
                  : undefined
              }
            />
          );
        },
      }),
      columnHelper.display({
        header: 'Value',
        minSize: 60,
        cell: (ps) => {
          return <ParticipantBehaviourRecordingValue recording={ps.row.original} />;
        },
      }),
      columnHelper.display({
        header: 'Actions',
        minSize: 60,
        maxSize: 200,
        cell: (ps) => {
          return (
            <ChakraV3.HStack w='full'>
              <ParticipantBehaviourRecordingEditButton
                participantId={participantId}
                participantBehaviours={[ps.row.original.participantBehaviour]}
                recording={ps.row.original}
              />
              <ParticipantBehaviourRecordingDeleteButton
                participantId={participantId}
                participantBehaviourId={ps.row.original.participantBehaviourId}
                recordingId={ps.row.original.id}
              />
            </ChakraV3.HStack>
          );
        },
      }),
    ];
  }, [participantId]);

  if (P.isEmpty(recordings)) {
    return (
      <ScrollArea w='full' h='full' bg='white' overflowY='auto' px={6}>
        <Empty title='No recordings found' />
      </ScrollArea>
    );
  }

  return (
    <ChakraV3.Stack h='full' gap={0} overflowY='hidden'>
      <ScrollArea w='full' h='full' bg='white' overflowY='auto' px={6}>
        <ChakraV3.Stack gap={8}>
          {orderedGroupedRecordings.map(([timestamp, recordings]) => (
            <ChakraV3.Stack key={timestamp} py={0} gap={0} borderBottomWidth='1px'>
              <FancyDate
                date={DateTime.fromISO(timestamp)}
                format={DateTime.DATE_SHORT}
                showHumanized={false}
                direction='row'
                fontSize='lg'
                fontWeight='semibold'
              />
              <DataTable
                columns={columns}
                data={recordings}
                size='sm'
                showHeader={false}
                showFooter={false}
              />
            </ChakraV3.Stack>
          ))}
        </ChakraV3.Stack>
      </ScrollArea>
      <ChakraV3.HStack px={6} py={2} borderTopWidth='1px'>
        <ChakraV3.Spacer />
        <Pagination {...pagination} />
      </ChakraV3.HStack>
    </ChakraV3.Stack>
  );
};
